import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  GraduationCap, Plus, FileText, CheckCircle2, XCircle, Loader2, X,
  Trash2, Upload, Pencil, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

/** Unified entry point — renders admin vs staff view based on role. */
export default function Training() {
  const { user } = useAuth();
  if (user.role === "admin") return <AdminTraining />;
  return <StaffTraining />;
}

/* ------------------------------- Staff view ------------------------------ */
function StaffTraining() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/training");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="staff-training-page">
      <div>
        <div className="accp-label">Learning</div>
        <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
          <GraduationCap size={28} className="inline mr-2 -mt-1" />
          Training
        </h1>
        <p className="text-muted-foreground mt-1">
          Complete required training and earn certifications automatically.
        </p>
      </div>

      {loading && (
        <div className="accp-card p-8 text-center text-muted-foreground">
          <Loader2 size={16} className="animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="accp-card p-10 text-center text-muted-foreground" data-testid="training-empty">
          <FileText size={22} className="inline mr-2 text-primary" />
          No training has been published yet.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <StaffTrainingCard key={r.id} row={r} onOpen={() => setOpenId(r.id)} />
        ))}
      </div>

      {openId && <StaffTrainingDetail id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
}

function StaffTrainingCard({ row, onOpen }) {
  const status = row.my_status?.status || "not_started";
  const pill = {
    not_started: { bg: "bg-[#EEEBF5]", fg: "text-[#5B3AA4]", label: "Not started" },
    passed:      { bg: "bg-[#DCF5D9]", fg: "text-[#4A7C1E]", label: `Completed · ${row.my_status?.score ?? ""}%` },
    failed:      { bg: "bg-[#FFF8E6]", fg: "text-[#7A5A00]", label: `Failed · ${row.my_status?.score ?? ""}% — try again` },
  }[status];
  return (
    <button
      onClick={onOpen}
      className="accp-card p-5 text-left hover:ring-2 hover:ring-primary/30 transition"
      data-testid={`training-card-${row.code}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="accp-label">{row.code}</div>
          <h3 className="text-lg font-semibold text-primary mt-0.5" style={{ fontFamily: "Outfit" }}>
            {row.title}
          </h3>
        </div>
        <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full ${pill.bg} ${pill.fg}`}>
          {pill.label}
        </span>
      </div>
      {row.description && (
        <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{row.description}</p>
      )}
      {row.cert_type && (
        <div className="text-[11px] text-[#4A7C1E] mt-2 flex items-center gap-1">
          <ShieldCheck size={11} /> Earns {row.cert_type} cert on passing
        </div>
      )}
    </button>
  );
}

function StaffTrainingDetail({ id, onClose }) {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/training/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const canSubmit = useMemo(() => {
    if (!data?.quiz) return false;
    const qs = data.quiz.questions || [];
    return qs.length > 0 && qs.every((q) => answers[q.id]);
  }, [answers, data]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/training/${id}/complete`, { answers });
      setResult(r.data);
      if (r.data.passed) toast.success(`Passed with ${r.data.score}%`);
      else toast.error(`Scored ${r.data.score}% — try again`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!data) {
    return (
      <div className="accp-modal-shell" onClick={onClose}>
        <div className="accp-modal-card text-center text-muted-foreground">
          <Loader2 size={16} className="animate-spin inline mr-2" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="accp-modal-shell items-start md:items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="accp-modal-card accp-modal-card-lg space-y-4"
           data-testid="training-detail">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="accp-label">{data.code}</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              {data.title}
            </h3>
            {data.description && <p className="text-xs text-muted-foreground mt-1">{data.description}</p>}
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {data.file && (
          <div className="accp-card p-3">
            <div className="accp-label">Reading material</div>
            <a
              href={data.file.data_url}
              target="_blank" rel="noreferrer"
              download={data.file.name}
              className="text-sm text-primary underline inline-flex items-center gap-1 mt-1"
              data-testid="training-download"
            >
              <FileText size={14} /> {data.file.name}
            </a>
          </div>
        )}

        {result ? (
          <div className={`rounded-xl p-5 text-center space-y-2 ${result.passed ? "compliance-valid" : "compliance-expired"}`}
               data-testid="quiz-result">
            {result.passed ? (
              <CheckCircle2 size={36} className="inline text-[#4A7C1E]" />
            ) : (
              <XCircle size={36} className="inline text-[#B71C1C]" />
            )}
            <div className="text-xl font-semibold">{result.passed ? "Passed" : "Not passed"} · {result.score}%</div>
            <div className="text-xs">Passing score: {result.passing_score}%</div>
            {result.cert_created && (
              <div className="text-xs text-[#4A7C1E] mt-2">
                <ShieldCheck size={14} className="inline mr-1" />
                Certification added to your profile
              </div>
            )}
            {!result.passed && (
              <button className="accp-btn-primary mt-2" onClick={() => { setResult(null); setAnswers({}); }}>
                Try again
              </button>
            )}
          </div>
        ) : data.quiz ? (
          <div className="space-y-4" data-testid="quiz-form">
            <div className="accp-label">Quiz · passing score {data.quiz.passing_score}%</div>
            {(data.quiz.questions || []).map((q, idx) => (
              <div key={q.id} className="accp-card p-4">
                <div className="text-sm font-semibold text-primary">
                  {idx + 1}. {q.text}
                </div>
                <div className="mt-2 space-y-1">
                  {q.options.map((opt) => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer hover:bg-accent/20 rounded p-1.5 text-sm">
                      <input
                        type="radio"
                        name={q.id}
                        value={opt}
                        checked={answers[q.id] === opt}
                        onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                        data-testid={`q-${q.id}-${opt}`}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="accp-btn-primary w-full"
              data-testid="submit-quiz"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Submit quiz
            </button>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No quiz attached to this training yet.</div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- Admin view ------------------------------ */
function AdminTraining() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/training")).data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(`Archive "${t.title}"? Staff will no longer see it; completion history is preserved.`)) return;
    try {
      await api.delete(`/training/${t.id}`);
      toast.success("Training archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-training-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="accp-label">Learning library</div>
          <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
            <GraduationCap size={28} className="inline mr-2 -mt-1" />
            Training
          </h1>
          <p className="text-muted-foreground mt-1">
            Publish training documents and quizzes. Passing grades auto-issue a certification.
          </p>
        </div>
        <button onClick={() => setNewOpen(true)} className="accp-btn-primary" data-testid="new-training-btn">
          <Plus size={14} /> New training
        </button>
      </div>

      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-accent/30 text-left">
              <tr>
                <th className="p-4 accp-label">Code</th>
                <th className="p-4 accp-label">Title</th>
                <th className="p-4 accp-label">Cert awarded</th>
                <th className="p-4 accp-label">Completions</th>
                <th className="p-4 accp-label text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">
                  No training documents yet. Click <b>New training</b> to add one.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-accent/20">
                  <td className="p-4 font-semibold text-primary">{r.code}</td>
                  <td className="p-4">
                    <div className="font-semibold">{r.title}</div>
                    {r.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{r.description}</div>}
                  </td>
                  <td className="p-4">
                    {r.cert_type ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#DCF5D9] text-[#4A7C1E] font-semibold">
                        {r.cert_type}
                      </span>
                    ) : <span className="text-muted-foreground text-xs">—</span>}
                  </td>
                  <td className="p-4 text-muted-foreground">{r.completion_count || 0}</td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditId(r.id)} className="accp-btn-ghost text-xs"
                              data-testid={`edit-training-${r.code}`}>
                        <Pencil size={12}/> Edit quiz
                      </button>
                      <button onClick={() => remove(r)} className="accp-btn-ghost text-xs text-destructive">
                        <Trash2 size={12}/> Archive
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {newOpen && <NewTrainingModal onClose={() => setNewOpen(false)} onCreated={(t) => {
        setNewOpen(false); load(); setEditId(t.id);
      }} />}
      {editId && <EditQuizModal id={editId} onClose={() => { setEditId(null); load(); }} />}
    </div>
  );
}

function NewTrainingModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [certType, setCertType] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, type: f.type, data_url: reader.result });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/training", {
        title: title.trim(), description: description.trim() || null,
        cert_type: certType.trim() || null, file,
      });
      toast.success(`${r.data.code} created — now add a quiz`);
      onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="accp-modal-shell items-start md:items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="accp-modal-card space-y-3"
           data-testid="new-training-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label">Learning</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>New training</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <label className="block">
          <span className="accp-label">Title</span>
          <input className="accp-input mt-1" value={title} onChange={(e) => setTitle(e.target.value)}
                 data-testid="training-title" />
        </label>
        <label className="block">
          <span className="accp-label">Description</span>
          <textarea className="accp-input mt-1" rows={2} value={description}
                    onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="block">
          <span className="accp-label">Certification awarded on pass (optional)</span>
          <input className="accp-input mt-1" value={certType} placeholder="e.g. CMT, CPR, First Aid"
                 onChange={(e) => setCertType(e.target.value)} data-testid="training-cert-type" />
        </label>
        <label className="block">
          <span className="accp-label">Document (PDF or image · max 10MB)</span>
          <input ref={inputRef} type="file" accept="application/pdf,image/*"
                 onChange={onPickFile} className="accp-input mt-1" data-testid="training-file" />
          {file && <div className="text-[11px] text-muted-foreground mt-1">{file.name}</div>}
        </label>
        <button onClick={submit} disabled={busy || !title.trim()}
                className="accp-btn-primary w-full" data-testid="create-training">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Create — add quiz next
        </button>
      </div>
    </div>
  );
}

function EditQuizModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [passing, setPassing] = useState(80);
  const [completions, setCompletions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, quiz, comps] = await Promise.all([
        api.get(`/training/${id}`),
        api.get(`/training/${id}/quiz`).catch(() => ({ data: null })),
        api.get(`/training/${id}/completions`).catch(() => ({ data: [] })),
      ]);
      setData(t.data);
      setCompletions(comps.data || []);
      if (quiz.data) {
        setQuestions(quiz.data.questions || []);
        setPassing(quiz.data.passing_score || 80);
      } else {
        setQuestions([{ id: "q1", text: "", options: ["", "", "", ""], correct: "" }]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addQ = () => setQuestions((qs) => [...qs, { id: `q${qs.length + 1}`, text: "", options: ["", "", "", ""], correct: "" }]);
  const removeQ = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));
  const updateQ = (i, patch) => setQuestions((qs) => qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const updateOpt = (i, oi, v) => setQuestions((qs) => qs.map((q, idx) => {
    if (idx !== i) return q;
    const opts = [...q.options]; opts[oi] = v;
    return { ...q, options: opts };
  }));

  const canSave = questions.length > 0 && questions.every((q) =>
    q.text.trim() && q.options.every((o) => o.trim()) && q.correct && q.options.includes(q.correct)
  );

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/training/${id}/quiz`, { passing_score: passing, questions });
      toast.success("Quiz saved");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return (
    <div className="accp-modal-shell" onClick={onClose}>
      <div className="accp-modal-card text-center text-muted-foreground">
        <Loader2 size={16} className="animate-spin inline mr-2" /> Loading…
      </div>
    </div>
  );

  return (
    <div className="accp-modal-shell items-start md:items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="accp-modal-card accp-modal-card-lg space-y-4"
           data-testid="edit-quiz-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label">{data.code}</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>{data.title}</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <label className="flex items-center gap-2">
          <span className="accp-label">Passing score</span>
          <input type="number" min={0} max={100} className="accp-input w-20" value={passing}
                 onChange={(e) => setPassing(Number(e.target.value))} />
          <span className="text-xs text-muted-foreground">%</span>
        </label>
        {questions.map((q, i) => (
          <div key={i} className="accp-card p-3 space-y-2" data-testid={`quiz-q-${i}`}>
            <div className="flex items-center justify-between">
              <span className="accp-label">Question {i + 1}</span>
              {questions.length > 1 && (
                <button onClick={() => removeQ(i)} className="text-xs text-destructive">
                  <Trash2 size={12} className="inline" /> remove
                </button>
              )}
            </div>
            <input className="accp-input" placeholder="Question text" value={q.text}
                   onChange={(e) => updateQ(i, { text: e.target.value })} />
            {q.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input type="radio" name={`correct-${i}`} checked={q.correct === opt && opt !== ""}
                       onChange={() => updateQ(i, { correct: opt })} />
                <input className="accp-input flex-1" placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                       value={opt} onChange={(e) => updateOpt(i, oi, e.target.value)} />
              </div>
            ))}
          </div>
        ))}
        <button onClick={addQ} className="accp-btn-ghost text-xs" data-testid="add-question">
          <Plus size={12} /> Add question
        </button>
        <button onClick={save} disabled={!canSave || busy}
                className="accp-btn-primary w-full" data-testid="save-quiz">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Save quiz
        </button>

        <div className="pt-4 border-t border-border">
          <div className="accp-label">Completions · {completions.length}</div>
          <div className="mt-2 max-h-48 overflow-y-auto">
            {completions.length === 0 ? (
              <div className="text-xs text-muted-foreground">No completions yet.</div>
            ) : (
              <ul className="space-y-1">
                {completions.map((c) => (
                  <li key={c.id} className="flex items-center justify-between text-xs py-1 border-b border-border/50">
                    <span>{c.user_name} <span className="text-muted-foreground">({c.user_code})</span></span>
                    <span className={c.passed ? "text-[#4A7C1E] font-semibold" : "text-[#B71C1C]"}>
                      {c.quiz_score}% {c.passed ? "✓" : "✗"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
